/**
 * Parses raw subject-ID input (CSV/TXT file contents OR pasted text) into
 * a deduplicated, validated set of UUIDs plus a structured report on what
 * was rejected. Pure function; safe to call from anywhere.
 *
 * PRD-001 DoD criterion: "After parsing the input, the form displays:
 * count of unique subject IDs loaded, count of duplicates removed, and a
 * list of any rows that failed UUID-format validation (with their original
 * row numbers)."
 *
 * Accepted layouts:
 * - One UUID per line.
 * - Comma-separated UUIDs on a single line, or comma-separated WITH newlines.
 * - Lines with a header (e.g. "subject_id") are tolerated — non-UUID rows
 *   are reported as invalid; the user can confirm or fix.
 * - Whitespace and quoting are stripped.
 * - Comparison is case-insensitive; output IDs are lowercased to match the
 *   API Lambda's normalization (`dedupAndValidate`).
 */

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface ParseInvalidRow {
    /** 1-based row number in the original input. */
    row: number;
    /** Original (trimmed but unquoted) value. */
    value: string;
}

export interface ParseSubjectsResult {
    /** Validated, deduplicated, lowercased UUIDs in first-occurrence order. */
    valid: string[];
    /** Count of duplicate UUIDs that were collapsed into one. */
    duplicateCount: number;
    /** Rows that failed UUID-format validation, in order encountered. */
    invalid: ParseInvalidRow[];
    /** Total non-empty rows seen (for sanity-checking the math). */
    totalRows: number;
}

export function parseSubjects(input: string): ParseSubjectsResult {
    const valid: string[] = [];
    const seen = new Set<string>();
    const invalid: ParseInvalidRow[] = [];
    let duplicateCount = 0;
    let totalRows = 0;

    if (!input) {
        return { valid, duplicateCount, invalid, totalRows };
    }

    let rowNum = 0;
    for (const rawLine of input.split(/\r?\n/)) {
        rowNum += 1;
        for (const rawCell of rawLine.split(",")) {
            const value = rawCell.replace(/^["']|["']$/g, "").trim();
            if (!value) continue;
            totalRows += 1;
            const lower = value.toLowerCase();
            if (!UUID_RE.test(lower)) {
                invalid.push({ row: rowNum, value });
                continue;
            }
            if (seen.has(lower)) {
                duplicateCount += 1;
                continue;
            }
            seen.add(lower);
            valid.push(lower);
        }
    }

    return { valid, duplicateCount, invalid, totalRows };
}
